import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Zap } from "lucide-react";
import QuoteForm from "./QuoteForm";
import WhatsAppIcon from "./WhatsAppIcon";
import { waLink } from "../../lib/contact";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const WA_MSG = "Hi Conexa Relay, I'd like a freight quote";

export default function FloatingActions() {
  const [open, setOpen] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    if (!isHome) { setShowQuote(true); return; }
    // On the homepage the hero already has a quote button — only reveal the
    // floating one once the visitor scrolls past the hero.
    const onScroll = () => setShowQuote(window.scrollY > window.innerHeight * 0.85);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  if (pathname.startsWith("/driver")) return null;

  return (
    <>
      <div className="hidden md:flex fixed right-4 bottom-5 z-[80] flex-col items-end gap-3">
        <AnimatePresence>
          {showQuote && (
            <motion.button
              key="quote"
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              data-testid="fab-instant-quote"
              onClick={() => setOpen(true)}
              className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold pl-4 pr-5 py-3 shadow-xl hover:shadow-[0_0_34px_hsl(var(--primary)/0.55)] transition-shadow"
            >
              <Zap className="w-4 h-4 group-hover:scale-110 transition-transform" />
              <span className="text-sm tracking-tight">Instant Quote</span>
            </motion.button>
          )}
        </AnimatePresence>
        <a
          data-testid="fab-whatsapp"
          href={waLink(WA_MSG)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-semibold pl-3 pr-4 py-3 shadow-xl hover:brightness-110 transition-all"
        >
          <WhatsAppIcon className="w-5 h-5" />
          <span className="text-sm tracking-tight hidden sm:inline">WhatsApp</span>
        </a>
      </div>

      {/* Mobile-only floating actions (bottom-left, clears the corner badge) */}
      <div className="md:hidden fixed left-3 bottom-3 z-[80] flex items-center gap-2">
        <AnimatePresence>
          {showQuote && (
            <motion.button
              key="m-quote"
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              data-testid="mobile-quote-bar"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold pl-4 pr-5 py-3 text-sm shadow-xl active:scale-95 transition-transform"
            >
              <Zap className="w-4 h-4" /> Get a Quote
            </motion.button>
          )}
        </AnimatePresence>
        <a
          data-testid="mobile-whatsapp-bar"
          href={waLink(WA_MSG)}
          target="_blank"
          rel="noreferrer"
          aria-label="Chat on WhatsApp"
          className="shrink-0 w-12 h-12 inline-flex items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl active:scale-95 transition-transform"
        >
          <WhatsAppIcon className="w-6 h-6" />
        </a>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="instant-quote-dialog" className="glass border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-extrabold text-2xl tracking-tight">Get an instant quote</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Tell us about your freight — we usually reply within hours.
            </DialogDescription>
          </DialogHeader>
          <QuoteForm tid="instant" />
        </DialogContent>
      </Dialog>
    </>
  );
}
